<template>
  <div>
    <Breadcrumbs :items="breadcrumbItems" page_title="live" />

    <SuccessDialog
      :show="successDialog"
      :text="successText"
      @close="onSuccessClose"
    />

    <v-container fluid class="schedule-live-page px-4 px-md-6 py-6">
      <div class="schedule-header mb-8">
        <div class="schedule-header__title">{{ pageTitle }}</div>
        <div class="schedule-header__path">{{ pagePath }}</div>

        <div class="schedule-header__actions mt-5">
          <v-btn text color="#64748b" class="schedule-header__cancel" @click="goBack">
            {{ $t("livefeature_cancel") }}
          </v-btn>
          <v-btn
            outlined
            color="#10b981"
            class="schedule-header__save"
            :loading="submitting"
            :disabled="submitting"
            @click="submitForm"
          >
            <v-icon left small>mdi-check</v-icon>
            {{ primaryActionLabel }}
          </v-btn>
        </div>
      </div>

      <v-form ref="scheduleForm" v-model="isValid" lazy-validation>
        <v-row>
          <v-col cols="12">
            <v-card flat class="schedule-surface rounded-xl pa-4 pa-md-6">
              <v-alert v-if="submitError" type="error" dense outlined class="mb-4">
                {{ submitError }}
              </v-alert>

              <v-row>
                <v-col cols="12">
                  <div class="field-block">
                    <label class="schedule-label" for="live-title">
                      {{ $t("livefeature_title") }} <span class="required-mark">*</span>
                    </label>
                    <v-text-field
                      id="live-title"
                      v-model="form.title"
                      :rules="requiredRules"
                      outlined
                      dense
                      hide-details="auto"
                      class="schedule-input"
                    />
                  </div>
                </v-col>

                <v-col cols="12">
                  <div class="field-block">
                    <label class="schedule-label" for="live-description">{{ $t("livefeature_description") }}</label>
                    <v-textarea
                      id="live-description"
                      v-model="form.description"
                      outlined
                      dense
                      rows="4"
                      hide-details="auto"
                      class="schedule-input"
                    />
                  </div>
                </v-col>

                <v-col cols="12" sm="6">
                  <div class="field-block">
                    <label class="schedule-label" for="live-date">
                      {{ $t("livefeature_date") }} <span class="required-mark">*</span>
                    </label>
                    <v-menu
                      v-model="dateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          id="live-date"
                          v-model="form.date"
                          :rules="requiredRules"
                          outlined
                          dense
                          readonly
                          hide-details="auto"
                          prepend-inner-icon="mdi-calendar"
                          class="schedule-input"
                          v-bind="attrs"
                          v-on="on"
                        />
                      </template>
                      <v-date-picker
                        v-model="form.date"
                        color="#10b981"
                        :min="todayIso"
                        @input="dateMenu = false"
                      />
                    </v-menu>
                  </div>
                </v-col>

                <v-col cols="12" sm="6">
                  <div class="field-block">
                    <label class="schedule-label" for="live-time-range">
                      {{ $t("livefeature_time_range") }} <span class="required-mark">*</span>
                    </label>
                    <v-dialog v-model="timeDialog" max-width="760">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          id="live-time-range"
                          :value="timeRange"
                          :rules="requiredRules"
                          outlined
                          dense
                          readonly
                          hide-details="auto"
                          prepend-inner-icon="mdi-clock-outline"
                          class="schedule-input"
                          v-bind="attrs"
                          v-on="on"
                        />
                      </template>

                      <v-card class="time-dialog-card pa-4 pa-md-5 rounded-xl">
                        <div class="time-dialog__title mb-2">{{ $t("livefeature_select_time_range") }}</div>
                        <div class="time-dialog__copy mb-5">
                          {{ $t("livefeature_time_range_copy") }}
                        </div>

                        <div class="time-dialog__preview mb-5">
                          <div class="time-dialog__preview-block">
                            <span class="time-dialog__preview-label">{{ $t("livefeature_start") }}</span>
                            <span class="time-dialog__preview-value">{{ formatDisplayTime(form.startTime) }}</span>
                          </div>
                          <v-icon color="#94a3b8" class="time-dialog__preview-arrow">mdi-arrow-right</v-icon>
                          <div class="time-dialog__preview-block">
                            <span class="time-dialog__preview-label">{{ $t("livefeature_end") }}</span>
                            <span class="time-dialog__preview-value">{{ formatDisplayTime(form.endTime) }}</span>
                          </div>
                        </div>

                        <v-row class="time-dialog__pickers">
                          <v-col cols="12" sm="6">
                            <div class="time-dialog__picker-card">
                              <div class="time-dialog__label mb-3">{{ $t("livefeature_start_time") }}</div>
                              <v-time-picker
                                v-model="form.startTime"
                                format="ampm"
                                full-width
                                @input="handleStartTimeChange"
                              />
                            </div>
                          </v-col>
                          <v-col cols="12" sm="6">
                            <div class="time-dialog__picker-card">
                              <div class="time-dialog__label mb-3">{{ $t("livefeature_end_time") }}</div>
                              <v-time-picker
                                v-model="form.endTime"
                                format="ampm"
                                full-width
                                :allowed-hours="allowedEndHours"
                                :allowed-minutes="allowedEndMinutes"
                                @input="handleEndTimeChange"
                              />
                            </div>
                          </v-col>
                        </v-row>
                        <div class="d-flex justify-end mt-4">
                          <v-btn text color="#64748b" @click="timeDialog = false">{{ $t("livefeature_done") }}</v-btn>
                        </div>
                      </v-card>
                    </v-dialog>
                  </div>
                </v-col>

                <v-col cols="12" sm="6">
                  <div class="field-block">
                    <label class="schedule-label" for="live-host">
                      {{ $t("livefeature_instructor_host") }} <span class="required-mark">*</span>
                    </label>
                    <v-autocomplete
                      id="live-host"
                      v-model="form.host"
                      :items="users"
                      :rules="requiredRules"
                      item-text="fullName"
                      item-value="userID"
                      outlined
                      dense
                      hide-details="auto"
                      :loading="userLoading"
                      prepend-inner-icon="mdi-account"
                      :placeholder="$t('livefeature_select_host')"
                      class="schedule-input"
                    />
                  </div>
                </v-col>

                <v-col cols="12" sm="6">
                  <div class="field-block">
                    <label class="schedule-label" for="live-cohost">{{ $t("livefeature_cohost") }}</label>
                    <v-autocomplete
                      id="live-cohost"
                      v-model="form.coHost"
                      :items="users"
                      item-text="fullName"
                      item-value="userID"
                      outlined
                      dense
                      hide-details="auto"
                      :loading="userLoading"
                      prepend-inner-icon="mdi-account-outline"
                      :placeholder="$t('livefeature_select_cohost')"
                      clearable
                      class="schedule-input"
                    />
                  </div>
                </v-col>

                <v-col cols="12">
                  <div class="field-block">
                    <label class="schedule-label" for="live-max-participants">
                      No. of Participants
                    </label>
                    <v-text-field
                      id="live-max-participants"
                      v-model.number="form.maxParticipants"
                      type="number"
                      min="1"
                      step="1"
                      outlined
                      dense
                      hide-details="auto"
                      prepend-inner-icon="mdi-account-group"
                      placeholder="e.g. 100"
                      class="schedule-input"
                      :disabled="chkAllUser"
                      :rules="[v => !v || (Number.isInteger(Number(v)) && Number(v) >= 1) || 'Must be a positive whole number']"
                    />
                  </div>
                </v-col>
              </v-row>

              <div class="settings-section mt-8">
                <div class="settings-section__heading mb-4">
                  <v-icon left color="#10b981">mdi-cog</v-icon>
                  {{ $t("livefeature_room_settings") }}
                </div>

                <div v-for="setting in roomSettings" :key="setting.key" class="settings-row">
                  <div>
                    <div class="settings-row__label">{{ setting.label }}</div>
                    <div class="settings-row__hint">{{ setting.hint }}</div>
                  </div>
                  <v-switch
                    v-model="form.settings[setting.key]"
                    color="#A6CC39"
                    inset
                    hide-details
                    class="settings-row__switch mt-0"
                  />
                </div>
              </div>

              <div class="settings-section mt-8">
                <v-row class="mt-5">
                  <v-col cols="12">
                    <p style="color: #47484b; font-size: 16px; margin-bottom: 10px;">
                      {{ $t("sendnotification") }}
                    </p>
                  </v-col>
                </v-row>

                <v-row class="mt-2">
                  <!-- Inbox -->
                  <v-col cols="12" sm="6" md="3">
                    <v-card
                      height="35px"
                      style="border: 1px dashed #c0c0c0; box-shadow: unset; border-radius: 4px; display: flex; align-items: center;"
                    >
                      <v-checkbox
                        class="pb-5 pl-3"
                        :label="$t('inbox')"
                        color="#a6cc39"
                        v-model="notificationSettings.inbox"
                        hide-details
                      ></v-checkbox>
                    </v-card>
                  </v-col>

                  <!-- Push Notification -->
                  <v-col cols="12" sm="6" md="3">
                    <v-card
                      height="35px"
                      style="border: 1px dashed #c0c0c0; box-shadow: unset; border-radius: 4px; display: flex; align-items: center;"
                    >
                      <v-checkbox
                        class="pb-5 pl-3"
                        color="#a6cc39"
                        :label="$t('pushnotification')"
                        v-model="notificationSettings.pushnoti"
                        hide-details
                      ></v-checkbox>
                    </v-card>
                  </v-col>

                  <!-- Line -->
                  <v-col cols="12" sm="6" md="3">
                    <v-card
                      height="35px"
                      style="border: 1px dashed #c0c0c0; box-shadow: unset; border-radius: 4px; display: flex; align-items: center;"
                    >
                      <v-checkbox
                        class="pb-5 pl-3"
                        color="#a6cc39"
                        :label="$t('line')"
                        v-model="notificationSettings.line"
                        hide-details
                      ></v-checkbox>
                    </v-card>
                  </v-col>

                  <!-- Email + language flags -->
                  <v-col cols="12" sm="6" md="3">
                    <v-card
                      height="35px"
                      style="border: 1.5px dashed #c0c0c0; box-shadow: unset; border-radius: 4px; display: flex; align-items: center;"
                    >
                      <v-row class="d-flex justify-start align-center" no-gutters>
                        <v-col class="d-flex justify-start align-center" cols="5">
                          <v-checkbox
                            class="pb-5 ml-3"
                            color="#a6cc39"
                            :label="$t('email')"
                            v-model="notificationSettings.email"
                            @change="handleNotificationEmailChange"
                            hide-details
                          ></v-checkbox>
                        </v-col>
                        <v-col class="d-flex justify-start align-center">
                          <v-row class="d-flex justify-start align-center ml-3">
                            <v-radio-group
                              v-model="selectedNotificationLanguage"
                              class="radio-group"
                              :disable="!notificationSettings.email"
                            >
                              <v-row rows="12" class="radiobutton">
                                <div class="radio-wrapper">
                                  <div v-if="!notificationSettings.email" class="radio-placeholder"></div>
                                  <v-radio v-else value="thai" class="radio-button"></v-radio>
                                </div>
                                <v-img src="@/assets/thailandflag.svg" width="35" height="22" class="ml-2"></v-img>
                                <div class="radio-wrapper ml-2">
                                  <div v-if="!notificationSettings.email" class="radio-placeholder"></div>
                                  <v-radio v-else class="radio-button" value="eng"></v-radio>
                                </div>
                                <v-img src="@/assets/ukflag.svg" width="35" height="22" class="ml-2"></v-img>
                              </v-row>
                            </v-radio-group>
                          </v-row>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>
                </v-row>

                <v-row class="mt-5" align="center">
                  <v-col cols="12" sm="auto" class="d-flex align-center pa-0 pl-3">
                    <p style="color: #47484b; font-size: 16px; margin: 0; padding-right: 5px;">
                      {{ $t("sendnotito") }}
                    </p>
                    <v-checkbox
                      class="ml-2 mt-0 pt-0"
                      v-model="chkAllUser"
                      color="#a6cc39"
                      hide-details
                    />
                    <p style="color: #47484b; font-size: 16px; margin: 0;">
                      {{ $t("alluser") }}
                    </p>
                  </v-col>
                  <v-spacer />
                  <v-col cols="12" sm="auto" class="d-flex justify-end pa-0 pr-3">
                    <v-btn
                      color="#A6CC39"
                      :disabled="chkAllUser"
                      style="border-radius: 8px;"
                      class="text-capitalize font-style white--text btn_hover_effect"
                      @click="userlistDialog = true"
                    >
                      <v-icon class="mr-2">mdi-account-multiple</v-icon>
                      {{ $t("selecteduserforsendnoti") }}
                    </v-btn>
                  </v-col>
                </v-row>

                <v-data-table
                  class="mt-5"
                  :items="selectedList"
                  :loading="userLoading"
                  :loading-text="$t('livefeature_loading_wait')"
                  :headers="selectedListUserHeader"
                  hide-default-footer
                >
                  <template v-slot:item.actions="{ item }">
                    <v-btn
                      color="#FF6060"
                      class="text-capitalize"
                      text
                      style="font-size: 16px"
                      @click="Delete(item)"
                    >
                      <v-icon class="mr-1">mdi-delete-outline</v-icon>
                      {{ $t("delete") }}
                    </v-btn>
                  </template>
                </v-data-table>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-form>
    </v-container>

    <v-dialog width="500" v-model="userlistDialog" scrollable persistent>
      <v-card>
        <v-card-title>
          <v-tabs hide-slider class="pt-3">
            <v-tab
              class="font-style text-capitalize"
              style="
                width: 165px;
                border-radius: 10px;
                border: 1px solid #9fc437;
              "
            >{{ $t("user") }}</v-tab>
          </v-tabs>
        </v-card-title>
        <v-card-text style="height: 500px">
          <v-row class="ma-1">
            <v-col>
              <div v-if="form.maxParticipants" class="d-flex align-center mb-2">
                <v-icon size="16" color="#A6CC39" class="mr-1">mdi-account-group</v-icon>
                <span style="font-size:0.82rem; color:#475569;">
                  Selected: <strong>{{ userSelected.length }}</strong> / <strong>{{ form.maxParticipants }}</strong>
                </span>
                <v-chip v-if="userSelected.length >= form.maxParticipants" x-small color="#fee2e2" text-color="#dc2626" class="ml-2">Limit reached</v-chip>
              </div>
              <v-data-table
                :items="usersWithDisabled"
                item-key="userID"
                :loading="userLoading"
                @toggle-select-all="SelectAllUser"
                :loading-text="$t('livefeature_loading_wait')"
                v-model="userSelected"
                show-select
                :headers="userListHeader"
                hide-default-footer
                @page-count="userpageCount = $event"
                :page.sync="userpage"
                item-disabled="_disabled"
              ></v-data-table>
            </v-col>
          </v-row>
          <v-row class="ma-1">
            <v-col cols="12" xs="12" sm="12" md="10" xl="10">
              <div class="d-flex text-left pl-3">
                <v-pagination
                  circle
                  v-model="userpage"
                  class="pagination"
                  :length="userpageCount"
                ></v-pagination>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="mt-3 pb-4">
          <v-spacer></v-spacer>
          <v-btn
            class="text-capitalize btnfont-style"
            style="
              border: 1px solid #c0c0c0;
              border-radius: 8px;
              color: #424242;
            "
            text
            @click="(userlistDialog = false), (userSelected = [])"
          >{{ $t("cancel") }}</v-btn>
          <v-btn
            class="text-capitalize btnfont-style btn_hover_effect"
            style="color: #ffff; border: 1px solid #a6cc39; border-radius: 8px"
            color="#A6CC39"
            width="100"
            @click="savePermissionList()"
          >{{ $t("save") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "LiveSchedule",

  async created() {
    this.populateFormFromRoute();

    if (this.isEditMode && this.currentSessionId) {
      await this.fetchSessionDetail();
    }
  },

  mounted() {
    this.getUserData();
    this.getCourseData();
  },

  data() {
    return {
      isValid: false,
      dateMenu: false,
      timeDialog: false,
      submitting: false,
      submitError: "",
      successDialog: false,
      successText: "",
      sessionDetail: null,
      courses: [],
      roomSettings: [
        {
          key: "autoRecord",
          label: this.$t("livefeature_auto_record_label"),
          hint: this.$t("livefeature_auto_record_hint"),
        },
        {
          key: "lobby",
          label: this.$t("livefeature_lobby_label"),
          hint: this.$t("livefeature_lobby_hint"),
        },
        {
          key: "autoAttendance",
          label: this.$t("livefeature_auto_attendance_label"),
          hint: this.$t("livefeature_auto_attendance_hint"),
        },
        {
          key: "allowExternal",
          label: this.$t("livefeature_allow_external_label"),
          hint: this.$t("livefeature_allow_external_hint"),
        },
        {
          key: "allowLearnerMedia",
          label: this.$t("livefeature_allow_learner_media_label"),
          hint: this.$t("livefeature_allow_learner_media_hint"),
        },
      ],
      notificationSettings: {
        inbox: false,
        pushnoti: false,
        line: false,
        email: false,
      },
      selectedNotificationLanguage: null,
      chkAllUser: false,
      selectedList: [],
      userlistDialog: false,
      internalUserSelected: [],
      users: [],
      userLoading: false,
      courseLoading: false,
      userpage: 1,
      userpageCount: 0,
      userdataList: [],
      form: {
        title: "",
        description: "",
        date: "",
        startTime: "",
        endTime: "",
        host: null,
        coHost: null,
        course: null,
        maxParticipants: null,
        settings: {
          autoRecord: false,
          lobby: true,
          autoAttendance: true,
          allowExternal: true,
          allowLearnerMedia: false,
        },
      },
      requiredRules: [(value) => !!value || this.$t("livefeature_required_field")],
    };
  },

  computed: {
    todayIso() {
      return new Date().toISOString().substring(0, 10);
    },

    usersWithDisabled() {
      const limit = parseInt(this.form.maxParticipants, 10);
      const limitActive = limit > 0 && this.userSelected.length >= limit;
      const selectedIds = new Set(this.userSelected.map((u) => u.userID));
      return this.users.map((u) => ({
        ...u,
        _disabled: limitActive && !selectedIds.has(u.userID),
      }));
    },

    userSelected: {
      get() {
        return this.internalUserSelected;
      },
      set(newVal) {
        const limit = parseInt(this.form.maxParticipants, 10);
        if (limit > 0 && newVal.length > limit) {
          // Preserve previously selected items — reject only the newly added one
          const prevIds = new Set(this.internalUserSelected.map((u) => u.userID));
          this.internalUserSelected = newVal.filter((u) => prevIds.has(u.userID));
        } else {
          this.internalUserSelected = newVal;
        }
      },
    },

    isEditMode() {
      return this.$route.query.mode === "edit";
    },

    currentSessionId() {
      return this.$route.query.sessionId || "";
    },

    breadcrumbItems() {
      return [
        {
          sidebar_tilte: "home",
        },
        {
          text: "live",
          href: "/home/live",
        },
        {
          text: this.isEditMode ? this.$t("livefeature_edit_session") : this.$t("schedule"),
        },
      ];
    },

    pageTitle() {
      return this.isEditMode ? this.$t("livefeature_edit_live_session") : this.$t("livefeature_schedule_live_session");
    },

    pagePath() {
      return this.isEditMode ? this.$t("livefeature_live_edit") : this.$t("livefeature_live_schedule");
    },

    primaryActionLabel() {
      return this.isEditMode ? this.$t("livefeature_save_changes") : this.$t("livefeature_save_send_invitation");
    },

    timeRange() {
      return `${this.formatDisplayTime(this.form.startTime)} - ${this.formatDisplayTime(this.form.endTime)}`;
    },

    createSessionEndpoint() {
      return `${this.web_url}LiveSession/AddSession`;
    },

    updateSessionEndpoint() {
      return `${this.web_url}LiveSession/UpdateSession`;
    },

    sessionDetailEndpoint() {
      return this.currentSessionId
        ? `${this.web_url}LiveSession/GetSessionById/${this.currentSessionId}`
        : "";
    },

    selectedListUserHeader() {
      return [
        {
          text: "#",
          align: "left",
          value: "no",
        },
        {
          text: this.$t("employeeId"),
          align: "left",
          value: "employeeID",
        },
        {
          text: this.$t("fullname"),
          align: "left",
          value: "fullName",
        },
        {
          text: this.$t("email"),
          align: "left",
          value: "email",
        },
        {
          text: this.$t("ph"),
          sortable: false,
          value: "mobile",
          align: "left",
        },
        {
          text: this.$t("group"),
          sortable: false,
          value: "group",
          align: "left",
        },
        {
          text: this.$t("delete"),
          align: "center",
          value: "actions",
        },
      ];
    },

    userListHeader() {
      return [
        {
          text: this.$t("name"),
          align: "left",
          value: "fullName",
        },
      ];
    },
  },

  methods: {
    populateFormFromRoute() {
      if (!this.isEditMode || !this.$route.query.sessionId) {
        return;
      }

      const { title, host, dateLabel, dateNumber, time, duration, category } = this.$route.query;
      const derivedDate = this.buildDateFromParts(dateLabel, dateNumber);
      const derivedEndTime = this.buildEndTime(time, duration);

      this.form = {
        ...this.form,
        title: title || this.form.title,
        host: host || this.form.host,
        date: derivedDate || this.form.date,
        startTime: time || this.form.startTime,
        endTime: derivedEndTime || this.form.endTime,
        description: category
          ? `${category} ${this.$t("livefeature_live_session_prefix").toLowerCase()} for ${title || this.form.title}`
          : this.form.description,
      };
    },

    buildDateFromParts(dateLabel, dateNumber) {
      const monthMap = {
        TODAY: 5,
        MAY: 5,
      };

      if (!dateLabel || !dateNumber) {
        return "";
      }

      const month = monthMap[String(dateLabel).toUpperCase()] || 5;
      const day = String(dateNumber).padStart(2, "0");

      return `2026-${String(month).padStart(2, "0")}-${day}`;
    },

    buildEndTime(startTime, duration) {
      if (!startTime || !duration) {
        return "";
      }

      const durationMinutes = parseInt(String(duration), 10);

      if (Number.isNaN(durationMinutes)) {
        return "";
      }

      const [hours, minutes] = String(startTime).split(":").map(Number);

      if (Number.isNaN(hours) || Number.isNaN(minutes)) {
        return "";
      }

      const totalMinutes = hours * 60 + minutes + durationMinutes;
      const endHours = String(Math.floor(totalMinutes / 60) % 24).padStart(2, "0");
      const endMinutes = String(totalMinutes % 60).padStart(2, "0");

      return `${endHours}:${endMinutes}`;
    },

    parseTimeToMinutes(timeValue) {
      if (!timeValue) {
        return null;
      }

      const [hours, minutes] = String(timeValue).split(":").map(Number);

      if (Number.isNaN(hours) || Number.isNaN(minutes)) {
        return null;
      }

      return hours * 60 + minutes;
    },

    isTimeRangeValid(startTime = this.form.startTime, endTime = this.form.endTime) {
      const startMinutes = this.parseTimeToMinutes(startTime);
      const endMinutes = this.parseTimeToMinutes(endTime);

      if (startMinutes === null || endMinutes === null) {
        return false;
      }

      return endMinutes > startMinutes;
    },

    normalizeEndTime(startTime = this.form.startTime) {
      if (this.isTimeRangeValid(startTime, this.form.endTime)) {
        return;
      }

      this.form.endTime = this.buildEndTime(startTime, 60) || this.form.endTime;
    },

    handleStartTimeChange(value) {
      this.form.startTime = value;
      this.normalizeEndTime(value);
    },

    handleEndTimeChange(value) {
      this.form.endTime = value;

      if (!this.isTimeRangeValid(this.form.startTime, value)) {
        this.form.endTime = this.buildEndTime(this.form.startTime, 60) || value;
      }
    },

    allowedEndHours(hour) {
      const startMinutes = this.parseTimeToMinutes(this.form.startTime);

      if (startMinutes === null) {
        return true;
      }

      return hour * 60 + 59 > startMinutes;
    },

    allowedEndMinutes(minute) {
      const startMinutes = this.parseTimeToMinutes(this.form.startTime);
      const selectedEndHour = Number(String(this.form.endTime || "").split(":")[0]);

      if (startMinutes === null || Number.isNaN(selectedEndHour)) {
        return true;
      }

      return selectedEndHour * 60 + minute > startMinutes;
    },

    formatDisplayTime(timeValue) {
      const totalMinutes = this.parseTimeToMinutes(timeValue);

      if (totalMinutes === null) {
        return "--:--";
      }

      const hours24 = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      const meridiem = hours24 >= 12 ? "PM" : "AM";
      const hours12 = hours24 % 12 || 12;

      return `${String(hours12).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${meridiem}`;
    },

    parseApiDate(value) {
      if (!value) {
        return null;
      }

      const normalizedValue =
        typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(value)
          ? `${value}Z`
          : value;

      const parsedDate = new Date(normalizedValue);

      if (Number.isNaN(parsedDate.getTime())) {
        return null;
      }

      return parsedDate;
    },

    formatDateForInput(dateValue) {
      if (!dateValue) {
        return "";
      }

      const parsedDate = this.parseApiDate(dateValue) || dateValue;

      if (!(parsedDate instanceof Date)) {
        return "";
      }

      const year = parsedDate.getFullYear();
      const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
      const day = String(parsedDate.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    },

    formatTimeForInput(dateValue) {
      if (!dateValue) {
        return "";
      }

      const parsedDate = this.parseApiDate(dateValue);

      if (parsedDate) {
        const hours = String(parsedDate.getHours()).padStart(2, "0");
        const minutes = String(parsedDate.getMinutes()).padStart(2, "0");

        return `${hours}:${minutes}`;
      }

      if (typeof dateValue === "string" && /^\d{2}:\d{2}/.test(dateValue)) {
        return String(dateValue).slice(0, 5);
      }

      return "";
    },

    handleNotificationEmailChange() {
      if (this.notificationSettings.email) {
        this.selectedNotificationLanguage = "thai";
        return;
      }

      this.selectedNotificationLanguage = null;
    },

    buildIsoDateTime(dateValue, timeValue) {
      if (!dateValue || !timeValue) {
        return null;
      }

      const normalizedTime = String(timeValue).length === 5 ? `${timeValue}:00` : String(timeValue);
      const [year, month, day] = String(dateValue).split("-").map(Number);
      const [hours, minutes, seconds = 0] = normalizedTime.split(":").map(Number);

      if (
        [year, month, day, hours, minutes, seconds].some((value) => Number.isNaN(value))
      ) {
        return null;
      }

      const localDateTime = new Date(year, month - 1, day, hours, minutes, seconds);

      if (Number.isNaN(localDateTime.getTime())) {
        return null;
      }

      return localDateTime.toISOString();
    },

    resolveUserId(value) {
      if (value === null || value === undefined || value === "") {
        return 0;
      }

      const parsedValue = parseInt(value, 10);

      if (!Number.isNaN(parsedValue)) {
        return parsedValue;
      }

      const matchedUser = this.users.find(
        (user) =>
          String(user.fullName || "").trim().toLowerCase() ===
          String(value || "").trim().toLowerCase()
      );

      if (!matchedUser) {
        return 0;
      }

      return parseInt(matchedUser.userID ?? matchedUser.employeeID ?? 0, 10) || 0;
    },

    syncSelectedHosts() {
      this.form.host = this.resolveUserId(this.form.host) || null;
      this.form.coHost = this.resolveUserId(this.form.coHost) || null;
    },

    applySessionDetail(session) {
      const scheduleDate = this.parseApiDate(
        session.scheduleDate || session.ScheduleDate || session.startTime || session.StartTime || ""
      );
      const startTime = this.parseApiDate(session.startTime || session.StartTime || "");
      const endTime = this.parseApiDate(session.endTime || session.EndTime || "");
      const topicId = session.topicID || session.TopicID;
      const normalizedTopicId = topicId ? String(topicId) : this.form.course;

      if (topicId) {
        this.ensureCourseOption(
          normalizedTopicId,
          session.courseName || session.topicName || `${this.$t("livefeature_course_prefix")} #${normalizedTopicId}`
        );
      }

      this.form = {
        ...this.form,
        title: session.title || "",
        description: session.description || "",
        date: this.formatDateForInput(scheduleDate),
        startTime: this.formatTimeForInput(startTime),
        endTime: this.formatTimeForInput(endTime),
        host: this.resolveUserId(session.hostUserID || session.HostUserID) || null,
        coHost: this.resolveUserId(session.coHostUserID || session.CoHostUserID) || null,
        course: normalizedTopicId,
        maxParticipants: session.noOfParticipants ?? session.NoOfParticipants ?? session.maxParticipants ?? session.MaxParticipants ?? null,
        settings: {
          autoRecord: Boolean(session.autoRecord ?? session.AutoRecord),
          lobby: Boolean(session.lobbyEnabled ?? session.LobbyEnabled),
          autoAttendance: Boolean(session.autoAttendance ?? session.AutoAttendance),
          allowExternal: Boolean(session.allowExternal ?? session.AllowExternal),
          allowLearnerMedia: Boolean(session.allowLearner ?? session.AllowLearner),
        },
      };

      this.notificationSettings = {
        inbox: Boolean(session.inboxFlag ?? session.InboxFlag),
        pushnoti: Boolean(session.pushNotiFlag ?? session.PushNotiFlag),
        line: Boolean(session.lineFlag ?? session.LineFlag),
        email: Boolean(session.emailFlag ?? session.EmailFlag),
      };
      this.selectedNotificationLanguage = session.emailLanguage || session.EmailLanguage || null;
      this.chkAllUser = Boolean(session.allUserFlag ?? session.AllUserFlag);
    },

    async fetchSessionDetail() {
      if (!this.sessionDetailEndpoint) {
        return;
      }

      try {
        const response = await this.$axios.get(this.sessionDetailEndpoint, {
          headers: {
            accept: "*/*",
          },
        });

        const session = response.data?.data || response.data;

        if (!session) {
          this.submitError = this.$t("livefeature_unable_load_live_session");
          return;
        }

        this.sessionDetail = session;
        this.applySessionDetail(session);
      } catch (error) {
        this.submitError =
          error.response?.data?.message ||
          error.response?.data?.title ||
          this.$t("livefeature_failed_load_live_session");
      }
    },

    buildLiveSessionPayload() {
      const auth = JSON.parse(localStorage.getItem("vivek_authenticated_user_data") || "null");
      const scheduleDate = this.buildIsoDateTime(this.form.date, this.form.startTime);
      const startTime = this.buildIsoDateTime(this.form.date, this.form.startTime);
      const endTime = this.buildIsoDateTime(this.form.date, this.form.endTime);
      const selectedUserIds = this.chkAllUser
        ? []
        : this.selectedList.map((user) => parseInt(user.employeeID, 10)).filter((userId) => !Number.isNaN(userId));

      return {
        companyID: localStorage.getItem("companyID") || "",
        title: this.form.title,
        description: this.form.description,
        scheduleDate,
        startTime,
        endTime,
        hostUserID: this.resolveUserId(this.form.host),
        coHostUserID: this.resolveUserId(this.form.coHost),
        topicID: parseInt(this.form.course, 10) || 0,
        noOfParticipants: parseInt(this.form.maxParticipants, 10) || null,
        roomID: `live-${Date.now()}`,
        status: 1,
        autoRecord: this.form.settings.autoRecord,
        lobbyEnabled: this.form.settings.lobby,
        autoAttendance: this.form.settings.autoAttendance,
        allowExternal: this.form.settings.allowExternal,
        allowLearner: this.form.settings.allowLearnerMedia,
        createdBy: auth?.memberId || localStorage.getItem("UserID") || "",
        userIds: selectedUserIds,
        inboxFlag: this.notificationSettings.inbox,
        pushNotiFlag: this.notificationSettings.pushnoti,
        emailFlag: this.notificationSettings.email,
        allUserFlag: this.chkAllUser,
        lineFlag: this.notificationSettings.line,
        emailLanguage: this.selectedNotificationLanguage || "",
        immediately: true,
        oneDay: true,
        oneHour: true,
        tenMinutes: true,
      };
    },

    buildUpdateSessionPayload() {
      const auth = JSON.parse(localStorage.getItem("vivek_authenticated_user_data") || "null");
      const scheduleDate = this.buildIsoDateTime(this.form.date, this.form.startTime);
      const startTime = this.buildIsoDateTime(this.form.date, this.form.startTime);
      const endTime = this.buildIsoDateTime(this.form.date, this.form.endTime);
      const selectedUserIds = this.chkAllUser
        ? []
        : this.selectedList
            .map((user) => parseInt(user.employeeID, 10))
            .filter((userId) => !Number.isNaN(userId));

      return {
        id: this.sessionDetail?.id || this.currentSessionId,
        companyID: this.sessionDetail?.companyID || localStorage.getItem("companyID") || "",
        title: this.form.title,
        description: this.form.description,
        scheduleDate,
        startTime,
        endTime,
        hostUserID: this.resolveUserId(this.form.host),
        coHostUserID: this.resolveUserId(this.form.coHost),
        topicID: parseInt(this.form.course, 10) || 0,
        noOfParticipants: parseInt(this.form.maxParticipants, 10) || null,
        roomID: this.sessionDetail?.roomID || `live-${Date.now()}`,
        status: this.sessionDetail?.status ?? 0,
        autoRecord: this.form.settings.autoRecord,
        lobbyEnabled: this.form.settings.lobby,
        autoAttendance: this.form.settings.autoAttendance,
        allowExternal: this.form.settings.allowExternal,
        allowLearner: this.form.settings.allowLearnerMedia,
        updatedBy: auth?.memberId || localStorage.getItem("UserID") || "",
        inboxFlag: this.notificationSettings.inbox,
        pushNotiFlag: this.notificationSettings.pushnoti,
        emailFlag: this.notificationSettings.email,
        allUserFlag: this.chkAllUser,
        lineFlag: this.notificationSettings.line,
        emailLanguage: this.selectedNotificationLanguage || "",
        immediately: true,
        oneDay: true,
        oneHour: true,
        tenMinutes: true,
        userIds: selectedUserIds,
      };
    },

    async savePermissionList() {
      const limit = parseInt(this.form.maxParticipants, 10);
      if (limit > 0 && this.userSelected.length > limit) {
        this.userSelected = this.userSelected.slice(0, limit);
      }
      this.userlistDialog = false;
      const user = [];

      for (const element of this.userSelected) {
        const filteruserdata = this.userdataList.find(
          (candidate) => candidate.employeeID == element.userID
        );

        if (filteruserdata == undefined) {
          user.push({
            employeeID: element.userID,
            fullName: element.fullName,
            email: element.email,
            mobile: element.mobile,
            group: element.department,
            index: element.index,
            no: user.length + 1,
          });
        } else {
          user.push({
            employeeID: filteruserdata.employeeID,
            fullName: filteruserdata.fullName,
            email: filteruserdata.email,
            mobile: filteruserdata.mobile,
            group: filteruserdata.group,
            index: filteruserdata.index,
            no: user.length + 1,
          });
        }
      }

      this.selectedList = user;
      this.userdataList = user;
    },

    SelectAllUser() {
      const limit = parseInt(this.form.maxParticipants, 10);
      if (this.userSelected == undefined) {
        this.userSelected = limit > 0 ? this.users.slice(0, limit) : this.users;
      } else if (this.userSelected.length == 0) {
        this.userSelected = limit > 0 ? this.users.slice(0, limit) : this.users;
      } else {
        this.userSelected = [];
      }
    },

    Delete(item) {
      this.selectedList.splice(
        this.selectedList.findIndex((selected) => selected.index === item.index),
        1
      );

      this.selectedList = this.selectedList.map((selected, index) => ({
        ...selected,
        no: index + 1,
      }));
    },

    async getUserData() {
      this.userLoading = false;
      const res = await this.$axios.get(
        `${this.web_urlV6}EmployeeEXT/GetEmployeeExtAllV4?id=` +
          localStorage.getItem("companyID")
      );

      this.users = res.data.data.map((user, index) => ({
        ...user,
        request: false,
        repeat: 0,
        menu: false,
        date: [],
        dateStatus: "Date Picker",
        index,
      }));

      this.syncSelectedHosts();

      this.userLoading = false;
    },

    ensureCourseOption(value, text) {
      if (value === null || value === undefined || value === "") {
        return;
      }

      const normalizedValue = String(value);
      const existingCourse = this.courses.find(
        (course) => String(course.value) === normalizedValue
      );

      if (existingCourse) {
        return;
      }

      this.courses = [
        ...this.courses,
        {
          text: text || `${this.$t("livefeature_course_prefix")} #${normalizedValue}`,
          value: normalizedValue,
        },
      ];
    },

    async getCourseData() {
      this.courseLoading = true;

      try {
        const response = await this.$axios.get(
          `${this.web_url}Courses/GetAllCourse?id=` + localStorage.getItem("companyID"),
          {
            headers: {
              accept: "*/*",
            },
          }
        );

        const rawCourses = Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data)
          ? response.data
          : [];

        this.courses = rawCourses
          .filter((course) => course.activeFlag !== false && course.ActiveFlag !== false)
          .map((course) => ({
            text: course.courseName || course.title || `${this.$t("livefeature_course_prefix")} #${course.id || course.courseID || ""}`,
            value: String(course.id || course.courseID || course.topicID || course.title || ""),
          }))
          .filter((course) => course.value !== "");

        if (this.isEditMode && this.form.course) {
          this.ensureCourseOption(
            this.form.course,
            this.sessionDetail?.courseName || this.sessionDetail?.topicName || `${this.$t("livefeature_course_prefix")} #${this.form.course}`
          );
        }
      } catch (error) {
        this.courses = [];
      } finally {
        this.courseLoading = false;
      }
    },

    goBack() {
      this.$router.push("/home/live");
    },

    async submitForm() {
      this.submitError = "";

      if (!this.$refs.scheduleForm.validate()) {
        return;
      }

      const payload = this.isEditMode
        ? this.buildUpdateSessionPayload()
        : this.buildLiveSessionPayload();

      if (!payload.scheduleDate || !payload.startTime || !payload.endTime) {
        this.submitError = this.$t("livefeature_select_valid_datetime");
        return;
      }

      if (!this.isTimeRangeValid()) {
        this.submitError = this.$t("livefeature_end_time_later");
        return;
      }

      if (this.isEditMode && !payload.id) {
        this.submitError = this.$t("livefeature_missing_session_id_update");
        return;
      }

      this.submitting = true;

      try {
        if (this.isEditMode) {
          await this.$axios.put(this.updateSessionEndpoint, payload, {
            headers: {
              accept: "*/*",
              "Content-Type": "application/json",
            },
          });
        } else {
          await this.$axios.post(this.createSessionEndpoint, payload, {
            headers: {
              accept: "*/*",
              "Content-Type": "application/json",
            },
          });
        }

        this.successText = this.isEditMode
          ? this.$t("livefeature_session_updated_success") || "Live session updated successfully."
          : this.$t("livefeature_session_created_success") || "Live session created successfully.";
        this.successDialog = true;
      } catch (error) {
        this.submitError =
          error.response?.data?.message ||
          error.response?.data?.title ||
          this.$t("livefeature_failed_create_live_session");
      } finally {
        this.submitting = false;
      }
    },
    onSuccessClose() {
      this.successDialog = false;
      this.$router.push("/home/live");
    },
  },
};
</script>

<style scoped>
.schedule-live-page {
  background: #f7fafc;
  min-height: 100%;
}

.schedule-header__title {
  color: #0f172a;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.1;
}

.schedule-header__path {
  color: #94a3b8;
  font-size: 0.96rem;
  margin-top: 8px;
}

.schedule-header__actions {
  align-items: center;
  display: flex;
}

.schedule-header__cancel {
  font-weight: 500;
  letter-spacing: 0;
  min-width: auto;
  text-transform: none;
}

.schedule-header__save {
  background: #f0fdf4;
  border-color: #bbf7d0 !important;
  color: #047857 !important;
  letter-spacing: 0;
  text-transform: none;
}

.schedule-surface {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.06);
}

.field-block {
  width: 100%;
}

.schedule-label {
  color: #0f172a;
  display: inline-block;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin-bottom: 10px;
  text-transform: uppercase;
}

.required-mark {
  color: #ef4444;
  font-size: 0.88rem;
}

.schedule-input ::v-deep .v-input__slot {
  background: #ffffff !important;
  border-radius: 14px !important;
  box-shadow: none !important;
}

.settings-section__heading {
  align-items: center;
  color: #0f172a;
  display: flex;
  font-size: 1rem;
  font-weight: 700;
}

.settings-row {
  align-items: center;
  border-top: 1px solid rgba(148, 163, 184, 0.14);
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 18px 0;
}

.settings-row__label {
  color: #0f172a;
  font-size: 0.96rem;
  font-weight: 600;
}

.settings-row__hint {
  color: #64748b;
  font-size: 0.9rem;
  margin-top: 4px;
}

.settings-row__switch {
  flex: 0 0 auto;
}

.radio-group {
  display: flex;
  align-items: center;
}

.radiobutton {
  margin-top: -10px;
}

.radio-wrapper {
  position: relative;
  width: 22px;
  height: 22px;
}

.radio-placeholder,
.radio-button {
  position: absolute;
  top: 2px;
  left: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
}

.radio-placeholder {
  background-color: #424242;
  opacity: 0.1;
  pointer-events: none;
}

.time-dialog__title {
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 700;
}

.time-dialog__copy {
  color: #64748b;
  font-size: 0.92rem;
  line-height: 1.5;
}

.time-dialog__label {
  color: #64748b;
  font-size: 0.88rem;
  font-weight: 600;
}

.time-dialog-card {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.time-dialog__preview {
  align-items: center;
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 16px;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  padding: 16px 18px;
}

.time-dialog__preview-block {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
}

.time-dialog__preview-label {
  color: #94a3b8;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.time-dialog__preview-value {
  color: #0f172a;
  font-size: 1rem;
  font-weight: 700;
}

.time-dialog__preview-arrow {
  flex: 0 0 auto;
}

.time-dialog__picker-card {
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 16px;
  padding: 12px;
}

::v-deep .time-dialog__picker-card .v-time-picker {
  box-shadow: none;
}

::v-deep .time-dialog__picker-card .v-picker__title {
  justify-content: center;
}

@media (max-width: 960px) {
  .schedule-header__actions {
    flex-wrap: wrap;
    gap: 10px;
  }
}

@media (max-width: 600px) {
  .schedule-header__title {
    font-size: 1.65rem;
  }

  .time-dialog__preview {
    align-items: flex-start;
    flex-direction: column;
  }

  .schedule-header__actions {
    align-items: stretch;
    flex-direction: column;
  }

  .schedule-header__actions .v-btn {
    margin-right: 0 !important;
  }

  .settings-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .ml-10,
  .ml-5 {
    margin-left: 0 !important;
    margin-top: 12px;
  }
}
</style>